/**
 * Cross-page banner shown to admins when the daily OpenAI model sync
 * surfaced new model ids the calling admin has not yet dismissed.
 *
 * Why a single shared component (and not inline-per-page): the banner
 * needs to render identically on every admin page so an admin who
 * lands on the dashboard or the customers list still sees the prompt
 * to review newly-discovered models. Mounting it once inside
 * `AdminAppShell` is the cleanest place for that.
 *
 * Performance shape:
 * - Backend computes the summary from the audit table on every call,
 *   which is fine because the dataset is small (one row per sync run).
 * - To avoid hammering the API on every navigation we cache the
 *   summary in `sessionStorage` for 60 seconds. Dismissal clears the
 *   cache so the banner immediately disappears on the next render.
 *
 * UX shape:
 * - Dismissed state is per-admin (server-side
 *   `User.LastAiModelBannerDismissedAt`). Dismissing on one device
 *   hides the banner on every device for the same admin.
 * - The banner links straight to `/admin/ai-models` so the admin can
 *   review and select with one click.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  getAiModelDiscoveries,
  dismissAiModelDiscoveries,
  type AiModelDiscoverySummary,
} from '../../services/adminAiModelsService';

const SESSION_CACHE_KEY = 'newModelsBanner.summary.v1';
const SESSION_CACHE_TTL_MS = 60_000;

interface CachedSummary {
  fetchedAtMs: number;
  summary: AiModelDiscoverySummary;
}

/**
 * Reads the cached summary from `sessionStorage` if it is still inside
 * the TTL window. Returns `null` on miss, expired, or parse failure
 * (defensive — a stale or corrupt cache should not crash the shell).
 */
const readCache = (): AiModelDiscoverySummary | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSummary;
    if (Date.now() - parsed.fetchedAtMs > SESSION_CACHE_TTL_MS) return null;
    return parsed.summary;
  } catch {
    return null;
  }
};

const writeCache = (summary: AiModelDiscoverySummary): void => {
  try {
    const payload: CachedSummary = { fetchedAtMs: Date.now(), summary };
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable in private browsing modes — in
    // that case the banner just refetches on the next mount, which is
    // an acceptable degradation.
  }
};

const clearCache = (): void => {
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // No-op — see writeCache for why.
  }
};

/**
 * Renders nothing when the calling admin has no undismissed
 * discoveries. Otherwise renders an inline alert with a CTA to the
 * AI Models admin page.
 */
export const NewModelsBanner: React.FC = () => {
  const [summary, setSummary] = useState<AiModelDiscoverySummary | null>(readCache);
  const [dismissing, setDismissing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    if (summary !== null) {
      // Cached — skip the network round-trip until the TTL elapses or
      // the admin navigates between sessions.
      return;
    }

    getAiModelDiscoveries()
      .then(result => {
        if (cancelled) return;
        writeCache(result);
        setSummary(result);
      })
      .catch(() => {
        // Failing the discovery fetch should not blow up the admin
        // shell; the banner just stays hidden until the next refresh.
        if (cancelled) return;
        setSummary({ latestDiscoveryAtUtc: null, discoveredCount: 0, discoveredExternalIds: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [summary]);

  if (!summary || summary.discoveredCount === 0) {
    return null;
  }

  const handleReview = () => {
    navigate('/admin/ai-models');
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await dismissAiModelDiscoveries();
      clearCache();
      setSummary({ latestDiscoveryAtUtc: null, discoveredCount: 0, discoveredExternalIds: [] });
    } catch {
      // Leave the banner up so the admin can retry.
      setDismissing(false);
    }
  };

  const previewIds = summary.discoveredExternalIds.slice(0, 3);
  const remaining = summary.discoveredExternalIds.length - previewIds.length;
  const idPreview = previewIds.join(', ') + (remaining > 0 ? `, +${remaining} more` : '');
  const noun = summary.discoveredCount === 1 ? 'model' : 'models';

  return (
    <Alert
      icon={<IconSparkles size={18} />}
      color="violet"
      variant="light"
      withCloseButton
      onClose={handleDismiss}
      title={`${summary.discoveredCount} new OpenAI ${noun} discovered`}
      mb="md"
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text size="sm" c="dimmed" style={{ flex: 1 }}>
          {idPreview ? `Newest: ${idPreview}.` : ''} Review and select on the AI Models page.
        </Text>
        <Button size="xs" variant="filled" color="violet" onClick={handleReview} disabled={dismissing}>
          Review
        </Button>
      </Group>
    </Alert>
  );
};
